import {
  BooleanField,
  ImageField,
  NumberField,
  ReferenceField,
  SelectField,
  Show,
  SimpleShowLayout,
  TextField,
  UrlField,
  useCanAccess,
} from 'react-admin';
import { ReactElement, ReactNode } from 'react';
import { BidResource } from '../../Entities/Bid';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid2 as Grid,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { BID_TYPE_CHOICES } from './utils';
import TzDateField from '../../Components/TzDate/TzDateField';
import { CurrencyAmountField } from '../../Components/CurrencyAmount/CurrencyAmountField';
import { ApprovalStatusField } from './ApprovalStatusField';

interface AccordionPanelProps {
  children: ReactNode;
  defaultExpanded?: boolean;
  label: string;
}

function AccordionPanel({
  children,
  defaultExpanded = false,
  label,
}: AccordionPanelProps): ReactElement {
  return (
    <Accordion defaultExpanded={defaultExpanded}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography component="span" variant="subtitle1">
          {label}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
}

export default function BidShow(): ReactElement {
  const categoryAccess = useCanAccess({ resource: 'categories', action: 'list' });
  const merchantAccess = useCanAccess({ resource: 'merchants', action: 'list' });
  const productAccess = useCanAccess({ resource: 'products', action: 'list' });

  const canListCategories = categoryAccess.canAccess ?? false;
  const canListMerchants = merchantAccess.canAccess ?? false;
  const canListProducts = productAccess.canAccess ?? false;

  const prefetch: string[] = [];
  if (canListCategories) prefetch.push('category');
  if (canListMerchants) prefetch.push('merchant');
  if (canListProducts) prefetch.push('product');

  return (
    <Show<BidResource> queryOptions={{ meta: { prefetch } }}>
      <AccordionPanel label="Approval status/details" defaultExpanded>
        <SimpleShowLayout>
          <TextField label="ID" source="id" />
          <TextField label="Name" source="attributes.name" />
          <ApprovalStatusField />
          <NumberField label="Bid CPC (cents)" source="attributes.bid_cents" />
          <TextField label="Remarks" source="attributes.remarks" />
        </SimpleShowLayout>
      </AccordionPanel>
      <AccordionPanel label="Bid status">
        <SimpleShowLayout>
          <SelectField choices={BID_TYPE_CHOICES} label="Bid Type" source="attributes.bid_type" />
          <BooleanField label="Active" source="attributes.active" />
          <TzDateField label="Starts At" source="attributes.starts_at" />
          <TzDateField label="Ends At" source="attributes.ends_at" />
        </SimpleShowLayout>
      </AccordionPanel>
      <AccordionPanel label="Offer details">
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 6 }}>
            <SimpleShowLayout>
              <TextField label="Name" source="attributes.name" />
              <TextField label="Description" source="attributes.description" />
              <ImageField label="Image" source="attributes.image_url" />
            </SimpleShowLayout>
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            <SimpleShowLayout>
              <ReferenceField
                label="Merchant"
                reference="merchants"
                source="relationships.merchant.data.id"
              >
                <TextField source="attributes.merchant_name" />
              </ReferenceField>
              <TextField label="Site ID" source="attributes.site_id" />
              <UrlField label="Offer URL" source="attributes.url" />
              <CurrencyAmountField label="Offer Price" source="attributes.offer_price" />
              <CurrencyAmountField label="Retail Price" source="attributes.retail_price" />
            </SimpleShowLayout>
          </Grid>
        </Grid>
      </AccordionPanel>
      <AccordionPanel label="GetPrice details">
        <SimpleShowLayout>
          <ReferenceField
            label="Category"
            reference="categories"
            source="relationships.category.data.id"
          >
            <TextField source="attributes.name" />
          </ReferenceField>
          <ReferenceField
            label="Product"
            reference="products"
            source="relationships.product.data.id"
          >
            <TextField source="attributes.name" />
          </ReferenceField>
        </SimpleShowLayout>
      </AccordionPanel>
    </Show>
  );
}
